import { useState } from "react";
import { Modal } from "semantic-ui-react";

import { useSelectedDealer } from "components";
import { createFormHelper } from "lib/Form";
import "modules/TyreDiscountsDashboard/components/AddUpdateTyreDiscountModal/AddUpdateTyreDiscountModal.scss";
import { AddUpdateTyreDiscountForm } from "modules/TyreDiscountsDashboard/components/AddUpdateTyreDiscountModal/components";
import { useAddTyreDiscountMutation, useUpdateTyreDiscountMutation } from "modules/TyreDiscountsDashboard/components/AddUpdateTyreDiscountModal/hooks";
import { toISOString } from "utils/dateHelperFunctions";

import type { TyreDiscount } from "models";

type AddUpdateTyreDiscountModalProps = {
  isOpen: boolean;
  tyreDiscount: TyreDiscount | null;
  onClose: () => void;
};

export type AddUpdateTyreDiscountFormValues = Pick<TyreDiscount, "name" | "active" | "discount" | "net_margin_euro" | "net_margin_percent" | "is_brand_discount_only">;

const FormHelper = createFormHelper<AddUpdateTyreDiscountFormValues>();

export const AddUpdateTyreDiscountModal = ({ isOpen, onClose, tyreDiscount }: AddUpdateTyreDiscountModalProps) => {
  const [hasErrors, setHasErrors] = useState(false);

  const { selectedDealer } = useSelectedDealer();
  const { mutateAsync: addTyreDiscount, isPending: isAddTyreDiscountPending } = useAddTyreDiscountMutation();
  const { mutateAsync: updateTyreDiscount, isPending: isUpdateTyreDiscountPending } = useUpdateTyreDiscountMutation();

  const handleClose = () => {
    onClose();
    setHasErrors(false);
  };

  const validateFormData = (values: AddUpdateTyreDiscountFormValues) => {
    const hasErrors =
      !values.name ||
      (!values.is_brand_discount_only && values.discount == null && values.net_margin_euro == null && values.net_margin_percent == null) ||
      (values.discount != null && (values.discount < 0 || values.discount > 99.99)) ||
      (values.net_margin_euro != null && (values.net_margin_euro < 0 || values.net_margin_euro > 99.99)) ||
      (values.net_margin_percent != null && (values.net_margin_percent < 0 || values.net_margin_percent > 99.99));

    setHasErrors(Boolean(hasErrors));
    return hasErrors;
  };

  const handleSubmit = async (values: AddUpdateTyreDiscountFormValues) => {
    if (validateFormData(values)) return;

    if (tyreDiscount) await updateTyreDiscount({ ...tyreDiscount, ...values });
    else {
      if (!selectedDealer?.id) return;

      await addTyreDiscount({
        ...values,
        created_on: toISOString(new Date()),
        updated_on: toISOString(new Date()),
        id: -1,
        dealer_id: selectedDealer.id,
        discount: values.discount,
        net_margin_euro: values.net_margin_euro,
        net_margin_percent: values.net_margin_percent
      });
    }

    handleClose();
  };

  return (
    <Modal open={isOpen} onClose={handleClose} closeOnDimmerClick={false}>
      <div className="AddUpdateTyreDiscountModal">
        <FormHelper.Form
          onSubmit={handleSubmit}
          defaultValues={
            tyreDiscount
              ? { ...tyreDiscount }
              : { active: false, discount: null, is_brand_discount_only: false, name: "", net_margin_euro: null, net_margin_percent: null }
          }
        >
          <AddUpdateTyreDiscountForm
            hasErrors={hasErrors}
            isPending={isAddTyreDiscountPending || isUpdateTyreDiscountPending}
            onClose={handleClose}
            onSubmit={handleSubmit}
            tyreDiscount={tyreDiscount}
          />
        </FormHelper.Form>
      </div>
    </Modal>
  );
};
